'use client'

import { track } from '@shared/analytics/events'
import { useEffect } from 'react'

// 미들웨어(withDeferredLogin)가 교환 결과를 실어 보내는 쿼리 키.
const DL_RESULT_QUERY_KEY = 'dl_result'
const DL_REASON_QUERY_KEY = 'dl_reason'

// withDeferredLogin 이 싣는 실패 사유. 미들웨어와 값이 어긋나면 전부 'unknown' 으로 뭉개지므로
// 여기서 화이트리스트로 좁혀 받는다.
const KNOWN_FAIL_REASONS = ['invalid_or_expired', 'exchange_error', 'exchange_unreachable'] as const

type KnownFailReason = (typeof KNOWN_FAIL_REASONS)[number]

const isKnownFailReason = (reason: string | null): reason is KnownFailReason =>
  KNOWN_FAIL_REASONS.includes(reason as KnownFailReason)

/**
 * deferred deeplink 자동 로그인 교환(success/fail)을 analytics 로 적재하는 경량 컴포넌트.
 *
 * 교환 자체는 서버 미들웨어(withDeferredLogin, Edge)에서 일어나고 거기선 track() 을 호출할 수 없다
 * (브라우저 런타임 없음). 미들웨어가 redirect URL 에 `?dl_result=success` 또는
 * `?dl_result=fail&dl_reason=...` 를 실어 보내면, 이 컴포넌트가 마운트 시 1회 읽어 track 후
 * history.replaceState 로 쿼리를 제거한다(성공/실패 무관, 새로고침/공유 시 중복 적재 방지).
 *
 * useSearchParams 대신 window.location.search 를 mount effect 에서 직접 읽어 Suspense 경계가
 * 필요 없게 한다(클라이언트 전용 1회 실행).
 */
export const DeferredLoginTracker = () => {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const result = params.get(DL_RESULT_QUERY_KEY)

    if (result !== 'success' && result !== 'fail') {
      return
    }

    if (result === 'success') {
      track('deferred_login_exchange_success', {})
    } else {
      const reason = params.get(DL_REASON_QUERY_KEY)
      track('deferred_login_exchange_fail', {
        reason: isKnownFailReason(reason) ? reason : 'unknown',
      })
    }

    // 적재 후 결과 쿼리 제거 — 새로고침/공유로 인한 중복 적재 방지.
    params.delete(DL_RESULT_QUERY_KEY)
    params.delete(DL_REASON_QUERY_KEY)
    const query = params.toString()
    const newUrl = window.location.pathname + (query ? `?${query}` : '') + window.location.hash
    window.history.replaceState(window.history.state, '', newUrl)
  }, [])

  return null
}
